/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import * as initializer from '@ion/applications/src/telemetry/APMInitializer';
import FeatureFlags from '../security/FeatureFlags';

/**
 * Provides an abstraction for determining if the APM Framework is to be enabled.
 * Currently, the Framework can be fully enabled via the APM Feature Flags; i.e.
 * "?features=apm", or enabled but only to provide logging of APM metrics; that
 * is, the measurements will not be emitted to any external endpoints. This mode
 * is enabled via the APM_DEBUG Feature Flag; i.e. "?features=apmdebug".
 *
 * One the APM Framework is to be enabled by default (i.e. not hidden behind a
 * Feature Flag), this function can be updated as follows:
 *
 * ```typescript
 *  // _app.tsx
 *   import {getAPMEnablement} from '../telemetry/apm';
 *   ...
 *
 *   export const getAPMEnablement = () => ({
 *     enableAPMFramework : true,
 *     enableAPMDebugOnly : false
 *   });
 * ```
 */
export const getAPMEnablement = () => (
  initializer.getAPMEnablement(FeatureFlags)
);

/**
 * Provides an abstraction for initializing the APM Framework within the application.
 *
 * One the APM Framework is to be enabled by default (i.e. not hidden behind a
 * Feature Flag), this function can be updated as follows:
 *
 * ```typescript
 *   // _app.tsx
 *  import {
 *    getAPMEnablement,
 *    initializeAPM,
 *    initializeAPMClients
 *   } from '../telemetry/apm';
 *   ...
 *
 *   const {enableAPMDebugOnly} = getAPMEnablement();
 *   logger.enable(isDebug() || isDev || enableAPMDebugOnly);
 *   ...
 *   initializeAPM();
 * ```
 *
 * @param enable When `true`, the APM Framework is to be enabled, otherwise, it is to
 *        be disabled.
 * @param debugMode  When `true`, the APM Framework is only to log results, that is,
 *        no results are to be emitted to an external endpoint.
 */
export const initializeAPM = () => (
  initializer.initializeAPM(FeatureFlags)
);

/**
 * Provides a utility for initializing additional APM Monitor Client registrations
 * post-initialization.
 *
 * ```typescript

 *   // _app.tsx
 *   ...
 *   const {enableAPMFramework, enableAPMDebugOnly} = getAPMEnablement();
 *   initializeAPM(enableAPMFramework, enableAPMDebugOnly);
 *   initializeAPMClients();
 * ```
 *
 */
export const initializeAPMClients = () => (
  initializer.initializeAPMClients()
);
